import React from 'react';
import UserImageCircle from "../../components/UserImageCircle";
import {useCandidate} from "../../providers/CandidateProvider";
import {COMPONENT_NOTES, themeFor} from "../profiles/sections/hire-confidence/HireConfidenceParts";

/**
 * Colour treatment for each criterion rating, mirroring the pill styling used
 * in the candidate comparison report.
 */
const RATING_STYLES = {
    'STRONG': 'bg-green-50 text-green-700 border-green-200',
    'MODERATE': 'bg-violet-50 text-violet-700 border-violet-200',
    'PARTIAL': 'bg-amber-50 text-amber-700 border-amber-200',
    'WEAK': 'bg-orange-50 text-orange-700 border-orange-200',
    'NOT DEMONSTRATED': 'bg-gray-100 text-gray-500 border-gray-200',
};

// Note: built as a plain string rather than via TB.mapClass — that helper
// enumerates its second argument's entries, so handing it a class string
// yields the string's character indices as class names instead of the colours.
const RatingPill = ({rating}) => (
    <span className={
        'inline-block whitespace-nowrap rounded border px-2 py-1 text-[0.65rem] font-semibold tracking-wide '
        + (RATING_STYLES[rating] || RATING_STYLES['NOT DEMONSTRATED'])
    }>
        {rating}
    </span>
);

const BulletList = ({title, items, accentClass}) => {
    if (!items?.length) {
        return null;
    }

    return (
        <div className={`border-l-4 ${accentClass} bg-white rounded-r px-4 py-3`}>
            <p className="text-[0.7rem] font-semibold tracking-widest text-gray-500 mb-2">
                {title}
            </p>
            <ul className="list-disc pl-4 m-0 flex flex-col gap-1.5">
                {TB.map(items, (item, i) => (
                    <li key={`${title}-${i}`} className="text-sm leading-snug">
                        {item}
                    </li>
                ))}
            </ul>
        </div>
    );
};

/**
 * Full AI assessment of a candidate against a role's scoring criteria: the
 * per-criterion ratings with their evidence, plus the narrative summary.
 *
 * Rendered inside the shared modal via TB.event('Modal@show').
 */
const CandidateAssessmentDetails = ({candidate: candidateProp, hideHeader = false, flush = false}) => {
    // Modal content is mounted outside the tile's provider tree, so the
    // candidate is normally handed in as a prop; the context lookup only
    // covers callers that render this inline.
    const context = useCandidate();
    const candidate = candidateProp || context?.candidate;
    const assessment = candidate?.engagementFitAssessment?.();

    if (!assessment) {
        return (
            <div className="p-6 text-center">
                <p className="text-base m-0">
                    This candidate has not been assessed for this role yet.
                </p>
            </div>
        );
    }

    const criteria = assessment.criteria || [];

    // Engagement Fit's own colour, from the Hire Confidence palette — the same
    // purple its row and panel carry on the profile.
    const theme = themeFor('engagementFit');

    // The modal needs its own inset; embedded in a page section the host
    // already supplies one, and doubling it strands the content.
    return (
        <div className={TB.mapClass('candidate-assessment-details flex flex-col gap-5', {
            'p-5 sm:p-7': !flush,
        })}>

            {/* Who and what, on one line — the same header the Role Match
                modal carries, so two panels opened from adjacent rows of the
                same tile introduce themselves the same way.

                Suppressed where the surrounding page already names the talent
                and shows the score — the profile section, as against the modal
                this was built for. */}
            {!hideHeader && (
                <div className={TB.mapClass(
                    `flex flex-row flex-wrap items-center gap-x-6 gap-y-3 border-b border-[#EDEEF2] px-5 py-4 sm:px-7 ${theme.soft}`,
                    // Breaks back out of the container's own inset, so the tint
                    // runs the full width of the modal the way the profile's
                    // accordion header runs the full width of its card.
                    { '-mx-5 -mt-5 sm:-mx-7 sm:-mt-7': !flush }
                )}>
                    <div className="flex min-w-0 flex-1 flex-row items-center gap-3">
                        <UserImageCircle
                            user={candidate}
                            hideTooltip={true}
                            size={48}
                            className="img-grayscale"
                        />

                        <div className="min-w-0">
                            <h5 className="mb-0 truncate">
                                {candidate.firstNamePlusInitial?.() || candidate.first_name}
                            </h5>
                            <p className="mb-0 truncate text-sm text-gray-500">
                                {candidate.job_title}
                            </p>
                        </div>
                    </div>

                    <div className="flex shrink-0 flex-row items-center gap-3">
                        <span
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base text-white"
                            style={{ backgroundColor: theme.hex }}
                        >
                            <i className={`fas ${theme.icon}`} />
                        </span>

                        <div className="min-w-0 max-w-[280px]">
                            <p className="mb-0 text-lg font-bold tracking-[-0.01em] text-[#0E0E14]">
                                Engagement Fit
                            </p>
                            <p className="mb-0 mt-0.5 text-xs leading-[1.45] text-[#8B8F9A]">
                                {COMPONENT_NOTES.engagementFit}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div>
                <h6 className="font-bold mb-2">Criteria ratings</h6>
                <div className="border rounded overflow-hidden">
                    <div className="hidden sm:flex flex-row bg-gray-50 border-b text-[0.65rem] font-semibold tracking-widest text-gray-500">
                        <div className="w-1/3 px-3 py-2">CRITERION</div>
                        <div className="w-[140px] px-3 py-2">RATING</div>
                        <div className="flex-1 px-3 py-2">EVIDENCE</div>
                    </div>
                    {TB.map(criteria, (criterion, i) => (
                        <div
                            key={criterion.criterion_id || `criterion-${i}`}
                            className={TB.mapClass(
                                'flex flex-col sm:flex-row gap-1 sm:gap-0 px-3 py-3 sm:px-0 sm:py-0',
                                {'border-t': i > 0}
                            )}
                        >
                            <div className="sm:w-1/3 sm:px-3 sm:py-3 font-semibold text-sm">
                                {criterion.name}
                            </div>
                            <div className="sm:w-[140px] sm:px-3 sm:py-3">
                                <RatingPill rating={criterion.rating}/>
                            </div>
                            <div className="sm:flex-1 sm:px-3 sm:py-3 text-sm text-gray-700 leading-snug">
                                {criterion.evidence}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex flex-col gap-3">
                <h6 className="font-bold mb-0">Fit assessment</h6>

                {assessment.key_assessment && (
                    <div className="border-l-4 border-primary bg-white rounded-r px-4 py-3">
                        <p className="text-[0.7rem] font-semibold tracking-widest text-gray-500 mb-2">
                            KEY ASSESSMENT
                        </p>
                        <p className="text-sm leading-snug m-0">
                            {assessment.key_assessment}
                        </p>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <BulletList
                        title="STRENGTHS AGAINST THIS ROLE"
                        items={assessment.strengths}
                        accentClass="border-green-500"
                    />
                    <BulletList
                        title="GAPS AGAINST THIS ROLE"
                        items={assessment.gaps}
                        accentClass="border-amber-500"
                    />
                </div>

                {assessment.recommendation && (
                    <div className="border-l-4 border-primary bg-white rounded-r px-4 py-3">
                        <p className="text-[0.7rem] font-semibold tracking-widest text-gray-500 mb-2">
                            RECOMMENDATION
                        </p>
                        <p className="text-sm leading-snug m-0">
                            {assessment.recommendation}
                        </p>
                    </div>
                )}
            </div>

            {assessment.assessed_at && (
                <p className="text-xs text-gray-400 text-center m-0">
                    Assessed {TB.moment(assessment.assessed_at).format('lll')}
                </p>
            )}
        </div>
    );
};

export default CandidateAssessmentDetails;